import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { Avatar } from '@treams/treams-ui/common/Avatar';
import { Count } from '@treams/treams-ui/common/Count';
import { Tooltip } from '@treams/treams-ui/common/Tooltip';
import { tokens } from '@treams/treams-ui/theme/tokens';

import { formatSalary } from '../../salary-development/formatSalary';
import { type RewardEmployee, type RewardRange, type SalaryDomain } from './buildRewardFunctions';

const AVATAR_SIZE = 24;
const BOX_HEIGHT = 24;
/** Axis plus the avatar lane, so a row without salaries matches one with people. */
export const SALARY_PLOT_HEIGHT = BOX_HEIGHT + tokens.gap.element + AVATAR_SIZE;
/** A person joins a count only when their avatar would cover that count. */
const CLUSTER_DISTANCE = AVATAR_SIZE;

type Side = 'below' | 'above';

type Person = Pick<RewardEmployee, 'id' | 'name' | 'firstName' | 'lastName'> & { salary: number };

type Cluster = { people: Person[]; position: number };

const Plot = styled.div`
	position: relative;
	margin: 0 ${AVATAR_SIZE / 2}px;
`;

const Lane = styled.div<{ $height: number }>`
	position: relative;
	height: ${(props) => props.$height}px;
`;

const Axis = styled.div`
	position: absolute;
	top: 50%;
	left: 0;
	right: 0;
	height: 2px;
	transform: translateY(-50%);
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.border.default};
`;

const AxisEnd = styled.div<{ $position: number }>`
	position: absolute;
	top: 50%;
	left: ${(props) => props.$position * 100}%;
	width: 2px;
	height: 12px;
	transform: translate(-50%, -50%);
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.border.strong};
`;

const ScaleBox = styled.div<{ $from: number; $to: number }>`
	position: absolute;
	top: 0;
	bottom: 0;
	left: ${(props) => props.$from * 100}%;
	width: ${(props) => (props.$to - props.$from) * 100}%;
	min-width: 2px;
	box-sizing: border-box;
	border: 1px solid ${tokens.info.border};
	border-radius: ${tokens.radius.xs}px;
	background: ${tokens.info.background};
`;

const MedianHit = styled(Tooltip)<{ $position: number }>`
	position: absolute;
	top: 0;
	bottom: 0;
	left: ${(props) => props.$position * 100}%;
	z-index: 1;
	width: 12px;
	transform: translateX(-50%);
`;

const MedianMark = styled.div`
	position: absolute;
	top: 0;
	bottom: 0;
	left: 50%;
	width: 2px;
	transform: translateX(-50%);
	background: ${tokens.info.text};
`;

const ScaleTooltip = styled(Tooltip)<{ $from: number; $to: number }>`
	position: absolute;
	top: 0;
	bottom: 0;
	left: ${(props) => props.$from * 100}%;
	width: ${(props) => Math.max(props.$to - props.$from, 0) * 100}%;
	min-width: 2px;
`;

const CountMarker = styled.span`
	display: inline-flex;
	align-items: center;
	height: ${AVATAR_SIZE}px;
`;

const Marker = styled.div<{ $position: number }>`
	position: absolute;
	top: 0;
	left: ${(props) => props.$position * 100}%;
	transform: translateX(-50%);
	line-height: 0;

	&:hover {
		z-index: 1;
	}
`;

export function medianSalary(amounts: number[]): number | undefined {
	if (amounts.length === 0) {
		return undefined;
	}

	const sorted = [...amounts].sort((left, right) => left - right);
	const middle = Math.floor(sorted.length / 2);

	return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function sideOf(amount: number, range?: RewardRange): Side | undefined {
	if (range?.startAmount != null && amount < range.startAmount) return 'below';
	if (range?.endAmount != null && amount > range.endAmount) return 'above';

	return undefined;
}

type Copy = (key: string, values?: Record<string, string | number>) => string;

export function outsideSalaryNote(
	amount: number,
	range: RewardRange | undefined,
	money: (amount: number) => string,
	copy: Copy,
): string | undefined {
	const side = sideOf(amount, range);

	if (side === 'below' && range?.startAmount != null) {
		return copy('analytics.reward.belowMinimum', {
			amount: money(range.startAmount - amount),
		});
	}

	if (side === 'above' && range?.endAmount != null) {
		return copy('analytics.reward.aboveMaximum', {
			amount: money(amount - range.endAmount),
		});
	}

	return undefined;
}

function outsideNote(
	person: Person,
	range: RewardRange | undefined,
	money: (amount: number) => string,
	copy: Copy,
): string | undefined {
	return outsideSalaryNote(person.salary, range, money, copy);
}

function personLabel(
	person: Person,
	range: RewardRange | undefined,
	money: (amount: number) => string,
	copy: Copy,
): string {
	const note = outsideNote(person, range, money, copy);
	const salary = `${person.name}: ${money(person.salary)}`;

	return note ? `${salary}, ${note}` : salary;
}

// One or two people keep their names. A larger cluster is a summary: the names
// are on the expanded row.
export function describeCluster(
	people: Person[],
	range: RewardRange | undefined,
	money: (amount: number) => string,
	copy: Copy,
): string[] {
	if (people.length === 1) {
		return [personLabel(people[0], range, money, copy)];
	}

	if (people.length === 2 && people[0].salary === people[1].salary) {
		const note = outsideNote(people[0], range, money, copy);
		const line = `${people[0].name}, ${people[1].name}: ${money(people[0].salary)}`;

		return [note ? `${line}, ${note}` : line];
	}

	if (people.length === 2) {
		return people.map((person) => personLabel(person, range, money, copy));
	}

	const amounts = people.map((person) => person.salary);
	const low = Math.min(...amounts);
	const high = Math.max(...amounts);
	const outside = people.filter((person) => sideOf(person.salary, range)).length;
	const lines = [
		copy('analytics.reward.genderGap.people', { count: people.length }),
		low === high ? money(low) : `${money(low)} – ${money(high)}`,
	];

	if (outside > 0) {
		lines.push(copy('analytics.reward.outsideScale', { count: outside }));
	}

	return lines;
}

function useWidth() {
	const ref = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(0);

	useEffect(() => {
		const element = ref.current;

		if (!element) {
			return;
		}

		const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));

		observer.observe(element);
		setWidth(element.getBoundingClientRect().width);

		return () => observer.disconnect();
	}, []);

	return [ref, width] as const;
}

// Walks the people from low to high salary. Someone joins the previous count only
// when their avatar would cover that count, not merely the neighbour before them.
// Otherwise a gentle spread collapses into one bubble. People outside the scale
// never join a count of people inside it. Without a width nothing merges.
export function clusterPeople(
	people: Person[],
	toTrack: (amount: number) => number,
	width: number,
	range?: RewardRange,
) {
	const clusters: (Cluster & { last: number })[] = [];

	for (const person of [...people].sort((left, right) => left.salary - right.salary)) {
		const position = toTrack(person.salary);
		const previous = clusters[clusters.length - 1];
		const previousPerson = previous?.people[previous.people.length - 1];
		const sameBand =
			!range ||
			!previousPerson ||
			sideOf(person.salary, range) === sideOf(previousPerson.salary, range);

		if (
			previous &&
			width > 0 &&
			sameBand &&
			Math.abs(position - previous.position) * width < CLUSTER_DISTANCE
		) {
			previous.people.push(person);
			previous.last = position;
			previous.position =
				previous.people.reduce((sum, member) => sum + toTrack(member.salary), 0) /
				previous.people.length;
		} else {
			clusters.push({ people: [person], position, last: position });
		}
	}

	return clusters;
}

export function SalaryRangeBar({
	label,
	range,
	people,
	domain,
	fullTimeLabel,
}: {
	label: string;
	range?: RewardRange;
	/** People with a full-time salary, drawn as avatars under the scale. */
	people: Person[];
	/** Shared axis for every row on the card. Amounts outside it sit at the edge. */
	domain: SalaryDomain;
	fullTimeLabel?: string;
}) {
	const { i18n, t } = useTranslation();
	const [ref, width] = useWidth();
	const span = domain.max - domain.min;
	const toTrack = (amount: number) =>
		span <= 0 ? 0 : Math.min(1, Math.max(0, (amount - domain.min) / span));
	const money = (amount: number) => formatSalary(Math.round(amount), i18n.language);

	const hasScale = range?.startAmount != null && range.endAmount != null;
	const median =
		people.length > 1 ? medianSalary(people.map((person) => person.salary)) : undefined;

	const valueNow =
		hasScale && range.amount != null
			? Math.round(Math.min(range.endAmount!, Math.max(range.startAmount!, range.amount)))
			: undefined;

	return (
		<Box flex={1} minWidth={0}>
			<Plot ref={ref}>
				<Lane
					$height={BOX_HEIGHT}
					role={valueNow == null ? undefined : 'meter'}
					aria-label={label}
					aria-valuemin={hasScale ? range.startAmount : undefined}
					aria-valuemax={hasScale ? range.endAmount : undefined}
					aria-valuenow={valueNow}
				>
					<Axis />
					<AxisEnd $position={0} />
					<AxisEnd $position={1} />
					{hasScale && (
						<ScaleTooltip
							$from={toTrack(range.startAmount!)}
							$to={toTrack(range.endAmount!)}
							position="top"
							dark
							portal
							fitContent
							content={
								<>
									{range.name}
									<br />
									{money(range.startAmount!)} – {money(range.endAmount!)}
									{fullTimeLabel ? (
										<>
											<br />
											{fullTimeLabel}
										</>
									) : null}
								</>
							}
						>
							<ScaleBox $from={0} $to={1} aria-label={label} />
						</ScaleTooltip>
					)}
					{median != null && (
						<MedianHit
							$position={toTrack(median)}
							position="top"
							dark
							portal
							fitContent
							content={
								<>
									{t('analytics.reward.median')}
									<br />
									{money(median)}
								</>
							}
						>
							<MedianMark />
						</MedianHit>
					)}
				</Lane>
				{people.length > 0 && (
					<Lane $height={AVATAR_SIZE} style={{ marginTop: tokens.gap.element }}>
						{clusterPeople(people, toTrack, width, range).map((cluster) => {
							const [first] = cluster.people;
							const lines = describeCluster(cluster.people, range, money, t);
							const labelText = lines.join('\n');

							return (
								<Marker key={first.id} $position={cluster.position}>
									<Tooltip
										content={
											<>
												{lines.map((line) => (
													<div key={line}>{line}</div>
												))}
											</>
										}
										position="top"
										dark
										portal
										fitContent
									>
										{cluster.people.length > 1 ? (
											<CountMarker aria-label={labelText}>
												<Count
													count={cluster.people.length}
													variant={sideOf(first.salary, range) ? 'warning' : undefined}
												/>
											</CountMarker>
										) : (
											<Avatar.Ring
												tone={sideOf(first.salary, range) ? 'warning' : undefined}
												aria-label={labelText}
											>
												<Avatar size={AVATAR_SIZE} user={first} />
											</Avatar.Ring>
										)}
									</Tooltip>
								</Marker>
							);
						})}
					</Lane>
				)}
			</Plot>
		</Box>
	);
}
